// src/integration/growth/config.ts — CLIENT side of the growth-config kill-switch.
//
// Fetches the server's GET /growth-config (flags only, no secrets) and merges it OVER the frozen-OFF
// default in src/config/monetization.ts. This is the only runtime source that can WIDEN the default —
// and even then isSurfaceLive()/isGrowthSdkAllowed() still re-check the interlocks, so a surface lights
// only when BOTH the server flag AND the client interlock gate agree. GROWTH_MASTER=off on the server
// returns an all-false payload, killing the whole growth stack with no app-store round-trip.
//
// FAIL-CLOSED everywhere: no API base, a network error, a non-2xx, a timeout, or a malformed body all
// leave the config at the guarded default (every growth surface stays dark). Never throws into boot.

import { applyRemoteGrowthConfig } from '../../config/monetization';

function apiBase(): string {
  const raw = (import.meta.env?.VITE_API_BASE_URL as string | undefined) ?? '';
  return raw.replace(/\/+$/, '');
}

/** Best-effort, short-timeout fetch of the served growth-config. Safe to call fire-and-forget at boot. */
export async function loadGrowthConfig(): Promise<void> {
  const base = apiBase();
  if (!base) return; // no backend wired → stay dark
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 4000);
    let json: unknown;
    try {
      const res = await fetch(`${base}/growth-config`, { signal: controller.signal });
      if (!res.ok) return; // fail closed on any non-2xx
      json = await res.json();
    } finally {
      clearTimeout(timer);
    }
    applyRemoteGrowthConfig(json); // pure merge; itself fail-closed on a malformed payload
  } catch {
    // network / parse / timeout / abort — stay at the frozen-OFF default (everything dark).
  }
}
